import type { z } from 'zod';
import {
  type ChipStatus,
  ChipStatusEnum,
  type ContactabilityFilter,
  ContactabilityFilterEnum,
  type UrgencyFilter,
  UrgencyFilterEnum,
} from '@/models/ui/filterChip.ts';
import {
  type LeadFilters,
  LeadFiltersSchema,
} from '@/models/ui/leadFilters.ts';
import { LeadStatusGroups } from '@/models/ui/leadStatusGroup.ts';
import { SearchQueryBrand, SearchQueryNone } from '@/models/ui/search.ts';

const FILTER_PARAM_KEYS = ['status', 'urgency', 'contactability', 'q'] as const;

const STATUS_ORDER = ChipStatusEnum.options;
const URGENCY_ORDER = UrgencyFilterEnum.options;
const CONTACTABILITY_ORDER = ContactabilityFilterEnum.options;

function parseMultiParam(searchParams: URLSearchParams, key: string): string[] {
  return searchParams
    .getAll(key)
    .flatMap((value) => value.split(','))
    .map((value) => value.trim())
    .filter(Boolean);
}

function parseEnumValues<T extends string>(
  values: readonly string[],
  schema: z.ZodType<T>,
  order: readonly T[],
): T[] {
  const validValues = values
    .map((value) => schema.safeParse(value))
    .filter((parsed): parsed is z.ZodSafeParseSuccess<T> => parsed.success)
    .map((parsed) => parsed.data);

  const deduped = [...new Set(validValues)];
  return [...deduped].sort((a, b) => order.indexOf(a) - order.indexOf(b));
}

function normalizeSearch(search: string | null): string {
  return SearchQueryBrand.parse(search ?? SearchQueryNone).trim();
}

export function readLeadFiltersFromSearchParams(
  searchParams: URLSearchParams,
): LeadFilters {
  const groupParam = searchParams.get('statusGroup');
  const parsedStatusGroup = groupParam
    ? LeadStatusGroups.all.find((group) => group.url === groupParam)
    : undefined;

  return LeadFiltersSchema.parse({
    statusGroup: parsedStatusGroup ?? LeadStatusGroups.InProgress,
    statuses: parseEnumValues(
      parseMultiParam(searchParams, 'status'),
      ChipStatusEnum,
      STATUS_ORDER,
    ),
    urgencies: parseEnumValues(
      parseMultiParam(searchParams, 'urgency'),
      UrgencyFilterEnum,
      URGENCY_ORDER,
    ),
    contactabilities: parseEnumValues(
      parseMultiParam(searchParams, 'contactability'),
      ContactabilityFilterEnum,
      CONTACTABILITY_ORDER,
    ),
    search: normalizeSearch(searchParams.get('q')),
  });
}

export function writeLeadFiltersToSearchParams(
  currentSearchParams: URLSearchParams,
  filters: LeadFilters,
): URLSearchParams {
  const nextParams = new URLSearchParams(currentSearchParams);

  nextParams.set(
    'statusGroup',
    filters.statusGroup?.url ?? LeadStatusGroups.InProgress.url,
  );

  if (filters.statuses.length > 0) {
    nextParams.set('status', [...filters.statuses].join(','));
  } else {
    nextParams.delete('status');
  }

  if (filters.urgencies.length > 0) {
    nextParams.set('urgency', [...filters.urgencies].join(','));
  } else {
    nextParams.delete('urgency');
  }

  if (filters.contactabilities.length > 0) {
    nextParams.set('contactability', [...filters.contactabilities].join(','));
  } else {
    nextParams.delete('contactability');
  }

  if (filters.search) {
    nextParams.set('q', filters.search);
  } else {
    nextParams.delete('q');
  }

  return nextParams;
}

export function normalizedLeadFilterSearchParams(
  searchParams: URLSearchParams,
): URLSearchParams {
  const filters = readLeadFiltersFromSearchParams(searchParams);
  return writeLeadFiltersToSearchParams(searchParams, filters);
}

export function toggleStatusFilter(
  filters: LeadFilters,
  status: ChipStatus,
): LeadFilters {
  const next = filters.statuses.includes(status)
    ? filters.statuses.filter((value) => value !== status)
    : [...filters.statuses, status];

  return LeadFiltersSchema.parse({
    ...filters,
    statuses: parseEnumValues(next, ChipStatusEnum, STATUS_ORDER),
  });
}

export function toggleUrgencyFilter(
  filters: LeadFilters,
  urgency: UrgencyFilter,
): LeadFilters {
  const next = filters.urgencies.includes(urgency)
    ? filters.urgencies.filter((value) => value !== urgency)
    : [...filters.urgencies, urgency];

  return LeadFiltersSchema.parse({
    ...filters,
    urgencies: parseEnumValues(next, UrgencyFilterEnum, URGENCY_ORDER),
  });
}

export function toggleContactabilityFilter(
  filters: LeadFilters,
  contactability: ContactabilityFilter,
): LeadFilters {
  const next = filters.contactabilities.includes(contactability)
    ? filters.contactabilities.filter((value) => value !== contactability)
    : [...filters.contactabilities, contactability];

  return LeadFiltersSchema.parse({
    ...filters,
    contactabilities: parseEnumValues(
      next,
      ContactabilityFilterEnum,
      CONTACTABILITY_ORDER,
    ),
  });
}

export function clearLeadFilterParams(
  currentSearchParams: URLSearchParams,
): URLSearchParams {
  const nextParams = new URLSearchParams(currentSearchParams);
  for (const key of FILTER_PARAM_KEYS) {
    nextParams.delete(key);
  }
  return nextParams;
}
